import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { 
  Upload, Download, Wand2, SlidersHorizontal, 
  Image as ImageIcon, Lock, RefreshCw, Check, X
} from 'lucide-react';

const VisualStudio = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [image, setImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [presets, setPresets] = useState([]);
  const [filters, setFilters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [adjustments, setAdjustments] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    warmth: 0
  });
  const [loading, setLoading] = useState(false);

  const tier = user?.subscription_tier || 'free';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';
  const hasAccess = !SHOW_PRICING || tier === 'pro' || tier === 'experience' || isBetaUser;

  useEffect(() => {
    fetchStudioData();
  }, []);

  const fetchStudioData = async () => {
    try {
      const [presetsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/visual-studio/presets`),
        axios.get(`${API}/visual-studio/templates`)
      ]);
      setPresets(presetsRes.data.presets);
      setFilters(presetsRes.data.filters);
      setTemplates(templatesRes.data.templates);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          setImage(img);
          setOriginalImage(img);
          drawImage(img);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const drawImage = (img, preset = null, filter = null, adj = adjustments) => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    const maxWidth = 800;
    const maxHeight = 600;
    
    let width = img.width;
    let height = img.height;
    
    if (width > maxWidth) {
      height = (maxWidth / width) * height;
      width = maxWidth;
    }
    if (height > maxHeight) {
      width = (maxHeight / height) * width;
      height = maxHeight;
    }
    
    canvas.width = width;
    canvas.height = height;
    
    // Apply CSS filters
    let filterString = '';
    filterString += `brightness(${adj.brightness}%) `;
    filterString += `contrast(${adj.contrast}%) `;
    filterString += `saturate(${adj.saturation}%) `;
    
    if (adj.warmth !== 0) {
      filterString += `sepia(${Math.abs(adj.warmth)}%) `;
    }
    
    if (preset) {
      const presetAdj = preset.adjustments;
      if (presetAdj.brightness) filterString = filterString.replace(/brightness\(\d+%\)/, `brightness(${100 + presetAdj.brightness}%)`);
      if (presetAdj.contrast) filterString = filterString.replace(/contrast\(\d+%\)/, `contrast(${100 + presetAdj.contrast}%)`);
      if (presetAdj.saturation) filterString = filterString.replace(/saturate\(\d+%\)/, `saturate(${100 + presetAdj.saturation}%)`);
    }
    
    ctx.filter = filterString;
    ctx.drawImage(img, 0, 0, width, height);
    
    // Apply color overlay filter
    if (filter) {
      ctx.globalCompositeOperation = 'overlay';
      ctx.fillStyle = filter.overlay;
      ctx.globalAlpha = filter.opacity;
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    }
  };

  const applyPreset = (preset) => {
    setSelectedPreset(preset);
    if (image) {
      drawImage(image, preset, selectedFilter, adjustments);
    }
  };

  const applyFilter = (filter) => {
    setSelectedFilter(filter);
    if (image) {
      drawImage(image, selectedPreset, filter, adjustments);
    }
  };

  const handleAdjustmentChange = (key, value) => {
    const newAdjustments = { ...adjustments, [key]: value[0] };
    setAdjustments(newAdjustments);
    if (image) {
      drawImage(image, selectedPreset, selectedFilter, newAdjustments);
    }
  };

  const resetAdjustments = () => {
    const defaultAdj = { brightness: 100, contrast: 100, saturation: 100, warmth: 0 };
    setAdjustments(defaultAdj);
    setSelectedPreset(null);
    setSelectedFilter(null);
    if (originalImage) {
      drawImage(originalImage, null, null, defaultAdj);
    }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = 'clierxter-visual-studio-export.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Image downloaded!');
  };

  const saveEdit = async () => {
    if (!hasAccess) {
      toast.error('Visual Studio requires Pro subscription');
      return;
    }
    
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/visual-studio/apply`, {
        preset: selectedPreset?.id,
        filter: selectedFilter?.id,
        adjustments
      }, { headers });
      toast.success('Edit saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  if (!hasAccess) {
    return (
      <DashboardLayout title="Visual Content Studio">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold p-16 text-center">
            <Lock className="text-[#D4AF37] mx-auto mb-6" size={60} />
            <h2 className="font-serif text-3xl text-white mb-4">{t('premium.proFeature')}</h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              {t('premium.visualStudioDesc')}
            </p>
            <Button onClick={() => navigate('/dashboard')} className="btn-gold px-10 py-4" data-testid="upgrade-to-pro-btn">
              {t('premium.upgradeToPro')}
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Visual Content Studio">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-luxury p-6 mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">PRO FEATURE</span>
              </div>
              <h2 className="font-serif text-2xl text-white">Edit Your Brand Visuals</h2>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => fileInputRef.current?.click()} className="btn-outline-gold px-6 py-3" data-testid="upload-image-btn">
                <Upload size={18} className="mr-2" />
                Upload Image
              </Button>
              {image && (
                <Button onClick={handleDownload} className="btn-gold px-6 py-3" data-testid="download-image-btn">
                  <Download size={18} className="mr-2" />
                  Download
                </Button>
              )}
            </div>
          </div>
          <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Canvas Area */}
          <div className="lg:col-span-2">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-6">
              {image ? (
                <div className="flex items-center justify-center bg-[#0A0A0A] min-h-[400px]">
                  <canvas ref={canvasRef} className="max-w-full" />
                </div>
              ) : (
                <div 
                  className="flex flex-col items-center justify-center min-h-[400px] border-2 border-dashed border-[#27272A] cursor-pointer hover:border-[#D4AF37]/40 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImageIcon className="text-[#27272A] mb-4" size={60} />
                  <p className="text-[#52525B] text-center">
                    Click to upload or drag and drop<br />
                    <span className="text-[#71717A] text-sm">PNG, JPG up to 10MB</span>
                  </p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Controls Sidebar */}
          <div className="space-y-6">
            {/* Presets */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-premium p-6">
              <div className="flex items-center gap-2 mb-4">
                <Wand2 className="text-[#E5C585]" size={18} />
                <h3 className="font-serif text-lg text-white">Presets</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {presets.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => applyPreset(preset)}
                    className={`p-3 text-left text-sm transition-colors ${
                      selectedPreset?.id === preset.id
                        ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#E5C585]'
                        : 'bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]'
                    }`}
                    data-testid={`preset-${preset.id}`}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Filters */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-premium p-6">
              <h3 className="font-serif text-lg text-white mb-4">Color Overlays</h3>
              <div className="grid grid-cols-2 gap-2">
                {filters.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => applyFilter(filter)}
                    className={`p-3 text-left text-sm flex items-center gap-2 transition-colors ${
                      selectedFilter?.id === filter.id
                        ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#E5C585]'
                        : 'bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]'
                    }`}
                    data-testid={`filter-${filter.id}`}
                  >
                    <div className="w-4 h-4 rounded-full border border-[#27272A]" style={{ backgroundColor: filter.overlay }} />
                    {filter.name}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Manual Adjustments */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="card-premium p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="text-[#E5C585]" size={18} />
                  <h3 className="font-serif text-lg text-white">Adjustments</h3>
                </div>
                <button onClick={resetAdjustments} className="text-[#52525B] hover:text-[#E5C585] transition-colors">
                  <RefreshCw size={16} />
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#A1A1AA]">Brightness</span>
                    <span className="text-[#52525B]">{adjustments.brightness}%</span>
                  </div>
                  <Slider
                    value={[adjustments.brightness]}
                    onValueChange={(val) => handleAdjustmentChange('brightness', val)}
                    min={50}
                    max={150}
                    step={1}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#A1A1AA]">Contrast</span>
                    <span className="text-[#52525B]">{adjustments.contrast}%</span>
                  </div>
                  <Slider
                    value={[adjustments.contrast]}
                    onValueChange={(val) => handleAdjustmentChange('contrast', val)}
                    min={50}
                    max={150}
                    step={1}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#A1A1AA]">Saturation</span>
                    <span className="text-[#52525B]">{adjustments.saturation}%</span>
                  </div>
                  <Slider
                    value={[adjustments.saturation]}
                    onValueChange={(val) => handleAdjustmentChange('saturation', val)}
                    min={0}
                    max={200}
                    step={1}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#A1A1AA]">Warmth</span>
                    <span className="text-[#52525B]">{adjustments.warmth > 0 ? '+' : ''}{adjustments.warmth}</span>
                  </div>
                  <Slider
                    value={[adjustments.warmth]}
                    onValueChange={(val) => handleAdjustmentChange('warmth', val)}
                    min={-50}
                    max={50}
                    step={1}
                    className="w-full"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Templates */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">TEMPLATES</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {templates.map((template) => (
              <div key={template.id} className="card-premium p-4 text-center hover:border-[#D4AF37]/40 transition-colors cursor-pointer">
                <div className="bg-[#0A0A0A] aspect-square mb-3 flex items-center justify-center border border-[#27272A]">
                  <span className="text-[#52525B] text-xs">
                    {template.dimensions.width}×{template.dimensions.height}
                  </span>
                </div>
                <p className="text-[#A1A1AA] text-xs">{template.name}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default VisualStudio;
